import { Input , Label } from "@roberttamas/ui";
import { useTranslation } from "react-i18next";

import type { TranslationKey } from "@/i18n";
import { TRANSLATION_GROUPS } from "@/lib/translations";

export interface TranslationImportFeedback {
  fileName: string;
  importedCount: number;
  unknownKeys: string[];
  missingRequiredKeys: string[];
  invalidValueKeys: string[];
}

interface TranslationKeysFormProps {
  keys: Record<string, string>;
  onChange?: (keys: Record<string, string>) => void;
  readOnly?: boolean;
}

/** Labels for the uploader's translation keys live under `translationKeysMeta.<key>`;
 * the cast is safe because the catalogs mirror TRANSLATION_GROUPS. */
function fieldLabelKey(key: string): TranslationKey {
  return `translationKeysMeta.${key}` as TranslationKey;
}

export function TranslationKeysForm({
  keys,
  onChange,
  readOnly = false,
}: TranslationKeysFormProps) {
  const { t } = useTranslation();
  return (
    <div className="space-y-8">
      {TRANSLATION_GROUPS.map((group) => (
        <section key={group.id} className="space-y-4">
          <h3 className="border-b pb-1 text-sm font-semibold text-foreground">
            {t(`translationKeysMeta.groups.${group.id}` as TranslationKey)}
          </h3>
          {group.keys.map((key) => (
            <div key={key} className="space-y-1">
              <Label htmlFor={`key-${key}`} className="text-xs">
                {t(fieldLabelKey(key))}
                <code className="ml-2 rounded bg-muted px-1 py-0.5 font-mono text-[10px] text-muted-foreground">
                  {key}
                </code>
              </Label>
              <Input
                id={`key-${key}`}
                value={keys[key] ?? ""}
                onChange={
                  onChange
                    ? (e) => onChange({ ...keys, [key]: e.target.value })
                    : undefined
                }
                readOnly={readOnly}
                required={!readOnly}
                className={readOnly ? "read-only:bg-muted/40" : undefined}
              />
            </div>
          ))}
        </section>
      ))}
    </div>
  );
}

export function TranslationImportFeedbackMessage({
  feedback,
}: {
  feedback: TranslationImportFeedback;
}) {
  const { t } = useTranslation();
  return (
    <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
      <p>
        {t("translationImport.imported", {
          count: feedback.importedCount,
          fileName: feedback.fileName,
        })}
      </p>
      {feedback.missingRequiredKeys.length > 0 && (
        <p>{t("translationImport.missingRequired", { count: feedback.missingRequiredKeys.length })}</p>
      )}
      {feedback.unknownKeys.length > 0 && (
        <p>{t("translationImport.unknownIgnored", { count: feedback.unknownKeys.length })}</p>
      )}
      {feedback.invalidValueKeys.length > 0 && (
        <p>{t("translationImport.invalidIgnored", { count: feedback.invalidValueKeys.length })}</p>
      )}
    </div>
  );
}
